import { useState, useEffect } from 'react';
import { MessageSquare, CalendarClock, CheckCircle, Clock, Star, ArrowRight } from 'lucide-react';
import api from '../Server';

function StatCard({ label, value, icon, accent, iconBg, loading, onClick }) {
  return (
    <div
      className="stat-card"
      onClick={onClick}
      style={{
        '--card-accent': accent,
        '--card-icon-bg': iconBg,
        cursor: 'pointer',
      }}
    >
      <div className="stat-card-header">
        <span className="stat-label">{label}</span>
        <div className="stat-icon">{icon}</div>
      </div>
      <div className="stat-value" style={{ opacity: loading ? 0.4 : 1 }}>
        {loading ? '0' : (value ?? 0)}
      </div>
      <div style={{
        display: 'flex', alignItems: 'center', gap: 4,
        marginTop: 10, fontSize: 11.5, fontWeight: 600,
        color: accent, opacity: 0.8,
      }}>
        View all <ArrowRight size={12} />
      </div>
    </div>
  );
}

export default function Dashboard({ onNavigate }) {
  const [counts, setCounts]       = useState(null);
  const [loadingCounts, setLoadingCounts] = useState(true);

  const [enquiries, setEnquiries] = useState([]);
  const [demos, setDemos]         = useState([]);
  const [loadingEnq, setLoadingEnq]   = useState(true);
  const [loadingDemo, setLoadingDemo] = useState(true);

  // ── GET /message/counts — all stat card numbers ───────────────────────────
  useEffect(() => {
    api.get('/message/counts')
      .then(res => {
        const raw = res.data;
        // Accept flat object or nested under data/result
        const data = raw?.data ?? raw?.result ?? raw;
        setCounts(data);
      })
      .catch(() => setCounts(null))
      .finally(() => setLoadingCounts(false));
  }, []);

  // ── Fetch recent enquiries ────────────────────────────────────────────────
  useEffect(() => {
    api.get('/message/getMessages')
      .then(res => {
        const raw = res.data;
        const list = Array.isArray(raw)            ? raw
          : Array.isArray(raw?.data)               ? raw.data
          : Array.isArray(raw?.messages)           ? raw.messages
          : Array.isArray(raw?.result)             ? raw.result
          : [];
        setEnquiries([...list].reverse());
      })
      .catch(() => setEnquiries([]))
      .finally(() => setLoadingEnq(false));
  }, []);

  // ── Fetch recent demos ────────────────────────────────────────────────────
  useEffect(() => {
    api.get('/booking/getDemoBookings')
      .then(res => {
        const raw = res.data;
        const list = Array.isArray(raw)            ? raw
          : Array.isArray(raw?.data)               ? raw.data
          : Array.isArray(raw?.bookings)           ? raw.bookings
          : Array.isArray(raw?.result)             ? raw.result
          : [];
        setDemos([...list].reverse());
      })
      .catch(() => setDemos([]))
      .finally(() => setLoadingDemo(false));
  }, []);

  // Helper — pull a count value, fallback to 0
  const getCount = (...keys) => {
    if (!counts) return 0;
    for (const k of keys) {
      if (counts[k] !== undefined && counts[k] !== null) return counts[k];
    }
    return 0;
  };

  return (
    <div className="page-content">
      <div className="page-header">
        <h2>Dashboard Overview</h2>
        <span style={{ fontSize: 13, color: 'var(--text-secondary)' }}>
          Welcome back! Here's what's happening.
        </span>
      </div>

      {/* ── 3 stat cards from /message/counts ── */}
      <div className="stats-grid" style={{ gridTemplateColumns: 'repeat(3, 1fr)' }}>
        <StatCard
          label="Total Enquiries"
          value={getCount('enquiries', 'messages', 'total_enquiries', 'message_count')}
          icon={<MessageSquare size={18} />}
          accent="#3B82F6"
          iconBg="rgba(59,130,246,0.12)"
          loading={loadingCounts}
          onClick={() => onNavigate?.('enquiries')}
        />
        <StatCard
          label="Demo Schedules"
          value={getCount('demos', 'bookings', 'total_demos', 'demo_count', 'booking_count')}
          icon={<CalendarClock size={18} />}
          accent="#8B5CF6"
          iconBg="rgba(139,92,246,0.12)"
          loading={loadingCounts}
          onClick={() => onNavigate?.('demos')}
        />
        <StatCard
          label="Testimonials"
          value={getCount('testimonials', 'reviews', 'total_reviews', 'review_count', 'testimonial_count')}
          icon={<Star size={18} />}
          accent="#F59E0B"
          iconBg="rgba(245,158,11,0.12)"
          loading={loadingCounts}
          onClick={() => onNavigate?.('testimonials')}
        />
      </div>

      {/* ── Recent activity ── */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 18 }}>

        {/* Recent Enquiries */}
        <div className="table-card">
          <div className="table-card-header">
            <h3>Recent Enquiries</h3>
            <span className="badge badge-blue">{enquiries.length} total</span>
          </div>
          <div style={{ padding: '0 0 4px' }}>
            {loadingEnq ? (
              <div style={{ padding: '20px', textAlign: 'center', color: 'var(--text-secondary)', fontSize: 13 }}>
                Loading...
              </div>
            ) : enquiries.length === 0 ? (
              <div style={{ padding: '20px', textAlign: 'center', color: 'var(--text-secondary)', fontSize: 13 }}>
                No enquiries yet.
              </div>
            ) : enquiries.slice(0, 4).map((e, i) => (
              <div
                key={e.id ?? i}
                style={{
                  display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                  padding: '12px 20px', borderBottom: '1px solid var(--border-light)',
                }}
              >
                <div>
                  <div style={{ fontSize: 13.5, fontWeight: 600 }}>
                    {e.name || e.full_name || '—'}
                  </div>
                  <div style={{ fontSize: 12, color: 'var(--text-secondary)' }}>
                    {e.subject || e.email || '—'}
                  </div>
                </div>
                <span className="badge badge-blue">
                  <MessageSquare size={10} />&nbsp;New
                </span>
              </div>
            ))}
          </div>
        </div>

        {/* Upcoming Demos */}
        <div className="table-card">
          <div className="table-card-header">
            <h3>Upcoming Demos</h3>
            <span className="badge badge-blue">{demos.length} total</span>
          </div>
          <div style={{ padding: '0 0 4px' }}>
            {loadingDemo ? (
              <div style={{ padding: '20px', textAlign: 'center', color: 'var(--text-secondary)', fontSize: 13 }}>
                Loading...
              </div>
            ) : demos.length === 0 ? (
              <div style={{ padding: '20px', textAlign: 'center', color: 'var(--text-secondary)', fontSize: 13 }}>
                No demos scheduled yet.
              </div>
            ) : demos.slice(0, 4).map((d, i) => (
              <div
                key={d.id ?? i}
                style={{
                  display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                  padding: '12px 20px', borderBottom: '1px solid var(--border-light)',
                }}
              >
                <div>
                  <div style={{ fontSize: 13.5, fontWeight: 600 }}>
                    {d.full_name || d.name || '—'}
                  </div>
                  <div style={{ fontSize: 12, color: 'var(--text-secondary)' }}>
                    {d.company || '—'} · {d.booking_date || d.demoDate || '—'}
                  </div>
                </div>
                <span className={`badge ${d.status === 'completed' ? 'badge-green' : 'badge-yellow'}`}>
                  {d.status === 'completed' ? <CheckCircle size={10} /> : <Clock size={10} />}
                  &nbsp;{d.status || 'pending'}
                </span>
              </div>
            ))}
          </div>
        </div>

      </div>
    </div>
  );
}
